import { FC } from "react";
import { useTheme } from "@mui/material";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { Button, IconButton, Typography } from "@kaltura/ds-react-components";
import { Info24Icon, ArrowRight24Icon, Clear24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { StyledTopBanner, StyledButtonsContainer, StyledTextContainer, StyledTopRow } from "./StyledComponents";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface TopBannerProps {
    /**
     * text to show on the banner (translated)
     */
    bannerText: string;

    /**
     * text to show on the button (translated)
     */
    buttonText: string;

    /**
     * link to open when clicking the button (attendee/studio view link)
     */
    buttonUrl: string;

    /**
     * should link be opened in the same tab or a new tab
     */
    buttonTarget: "_self" | "_blank";

    /**
     * Optional callback when the close button is clicked
     */
    onClose?: () => void;

    /**
     * value to send with analytics on button click (e.g., "Studio View" or "Attendee View")
     */
    ctaAnalyticsValue: string;
    closeButtonAnalyticsValue: string;
}

export const TopBanner: FC<TopBannerProps> = ({
    bannerText,
    buttonText,
    buttonUrl,
    buttonTarget,
    onClose,
    ctaAnalyticsValue,
    closeButtonAnalyticsValue,
}) => {
    const theme = useTheme();
    const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const sendButtonAnalytics = useButtonAnalytics();

    const clickHandler = () => {
        sendButtonAnalytics(ctaAnalyticsValue, ButtonClickAnalyticsType.LAUNCH);
    };

    const closeHandler = () => {
        sendButtonAnalytics(closeButtonAnalyticsValue, ButtonClickAnalyticsType.CLOSE);
        onClose?.();
    };

    const actionButton = (
        <Button
            startIcon={<ArrowRight24Icon />}
            href={buttonUrl}
            target={buttonTarget}
            className="kms-ds-top-banner-button-attendee-view"
            onClick={clickHandler}
        >
            {buttonText}
        </Button>
    );

    const closeButton = (
        <IconButton
            variant={"borderless"}
            onClick={closeHandler}
            aria-label={translate("Close notification")}
            className="kms-ds-top-banner-button-close"
        >
            <Clear24Icon />
        </IconButton>
    );

    return (
        <StyledTopBanner role="region" className="kms-ds-live-studio-top-banner">
            <StyledTopRow>
                <Info24Icon />
                <StyledTextContainer>
                    <Typography variant={"body2"}>{bannerText}</Typography>
                </StyledTextContainer>

                {!isXsScreen ? (
                    <StyledButtonsContainer>
                        {actionButton}
                        {closeButton}
                    </StyledButtonsContainer>
                ) : (
                    closeButton
                )}
            </StyledTopRow>
            {isXsScreen && actionButton}
        </StyledTopBanner>
    );
};

export default TopBanner;
